import React, { useEffect, useMemo, useState } from "react";
import api, { formatTL, API } from "../lib/api";
import ProductImage from "../components/ProductImage";
import LabelPrintModal from "../components/LabelPrintModal";
import {
  Pencil,
  Trash2,
  Plus,
  Minus,
  Search,
  FileDown,
  FileText,
  AlertTriangle,
  X,
  Tag,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

function StockBadge({ stock, min }) {
  if (stock <= 0)
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200 font-medium">
        Stokta Yok
      </span>
    );
  if (stock <= min)
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-medium">
        <AlertTriangle className="w-3 h-3" /> Düşük
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-medium">
      Yeterli
    </span>
  );
}

function EditModal({ product, categories, onClose, onSaved }) {
  const [form, setForm] = useState({ ...product });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/label/product/${product.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const safeSku = (product.sku || "urun").replace(/[^A-Za-z0-9._-]/g, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeSku}-barkod.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Ürün bulunamadı" : "PDF indirilemedi");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        feature: form.feature,
        min_stock: Number(form.min_stock),
        cost_price: Number(form.cost_price),
        sale_price: Number(form.sale_price),
        description: form.description,
        category: form.category,
        sku: form.sku,
        barcode: form.barcode,
      };
      const { data } = await api.patch(`/products/${product.id}`, payload);
      toast.success("Ürün güncellendi");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-2xl border border-stone-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h3 className="font-serif-display text-2xl">Ürünü Düzenle</h3>
          <button
            onClick={onClose}
            data-testid="edit-modal-close"
            className="p-2 rounded-md hover:bg-stone-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            ["name", "Ürün Adı", "text"],
            ["feature", "Özellik", "text"],
            ["sku", "Ürün Kodu", "text"],
            ["barcode", "Barkod", "text"],
            ["cost_price", "Alış Fiyatı (₺)", "number"],
            ["sale_price", "Satış Fiyatı (₺)", "number"],
            ["min_stock", "Min. Stok", "number"],
          ].map(([k, label, type]) => (
            <div key={k}>
              <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">
                {label}
              </label>
              <input
                data-testid={`edit-${k}`}
                type={type}
                value={form[k] ?? ""}
                onChange={(e) => set(k, e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400"
              />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">
              Kategori
            </label>
            <select
              data-testid="edit-category"
              value={form.category || "Diğer"}
              onChange={(e) => set("category", e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400 bg-white"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">
              Açıklama
            </label>
            <textarea
              data-testid="edit-description"
              rows={3}
              value={form.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-stone-200">
          <button
            data-testid="edit-pdf"
            onClick={downloadPdf}
            className="px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm inline-flex items-center gap-1.5 hover:bg-stone-50 mr-auto"
            title="Barkod PDF İndir"
          >
            <FileText className="w-4 h-4" /> Barkod PDF İndir
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-stone-200 text-sm hover:bg-stone-50"
          >
            İptal
          </button>
          <button
            data-testid="edit-save"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [priceField, setPriceField] = useState("sale_price");
  const [editing, setEditing] = useState(null);
  const [labelFor, setLabelFor] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadPdf = async (p) => {
    try {
      const res = await api.get(`/label/product/${p.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const safeSku = (p.sku || "urun").replace(/[^A-Za-z0-9._-]/g, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeSku}-barkod.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
    } catch (e) {
      const msg = e?.response?.status === 404 ? "Ürün bulunamadı" : "PDF indirilemedi";
      (await import("sonner")).toast.error(msg);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { q, category: cat };
      if (stockFilter !== "all") params.stock_filter = stockFilter;
      if (minPrice) params.min_price = Number(minPrice);
      if (maxPrice) params.max_price = Number(maxPrice);
      if (minPrice || maxPrice) params.price_field = priceField;
      const [prodRes, catRes] = await Promise.all([
        api.get("/products", { params }),
        api.get("/categories"),
      ]);
      setItems(prodRes.data);
      setCategories(catRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [cat, stockFilter, priceField]);

  const adjust = async (p, amount) => {
    try {
      const { data } = await api.post(`/products/${p.id}/adjust`, {
        amount,
        type: amount >= 0 ? "giris" : "cikis",
        note: amount >= 0 ? "Hızlı stok artırma" : "Hızlı stok azaltma",
      });
      setItems((prev) => prev.map((it) => (it.id === p.id ? data : it)));
      toast.success(amount > 0 ? "Stok artırıldı" : "Stok azaltıldı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const removeItem = async (p) => {
    if (!window.confirm(`"${p.name}" ürünü silinsin mi?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      setItems((prev) => prev.filter((it) => it.id !== p.id));
      toast.success("Ürün silindi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silme başarısız");
    }
  };

  const doExport = () => {
    const token = localStorage.getItem("rivaco_token") || "";
    window.open(
      `${API}/export/products.xlsx?auth=${encodeURIComponent(token)}`,
      "_blank"
    );
  };

  const totalStock = useMemo(
    () => items.reduce((s, i) => s + Number(i.stock || 0), 0),
    [items]
  );

  return (
    <div data-testid="products-root">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
            ENVANTER
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900">
            Ürünler
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {items.length} ürün · Toplam {totalStock} adet stok
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={doExport}
            data-testid="export-xlsx-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-sm"
          >
            <FileDown className="w-4 h-4" /> Excel'e Aktar
          </button>
          <Link
            to="/yeni-urun"
            data-testid="new-product-cta"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg gold-gradient text-white text-sm"
          >
            + Yeni Ürün
          </Link>
        </div>
      </div>

      <div className="card-lux p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Ürün adı, kod, barkod ile ara..."
              className="w-full pl-9 pr-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400"
            />
          </div>
          <select
            data-testid="filter-category"
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="px-3 py-2 border border-stone-200 rounded-lg bg-white outline-none focus:border-amber-400 text-sm"
          >
            <option value="all">Tüm Kategoriler</option>
            {categories.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            data-testid="filter-stock"
            value={stockFilter}
            onChange={(e) => setStockFilter(e.target.value)}
            className="px-3 py-2 border border-stone-200 rounded-lg bg-white outline-none focus:border-amber-400 text-sm"
          >
            <option value="all">Stok Durumu (Tümü)</option>
            <option value="in_stock">Stokta olanlar</option>
            <option value="out_of_stock">Stokta olmayanlar</option>
            <option value="low_stock">Düşük stok</option>
          </select>
          <div className="flex items-center gap-1">
            <input
              data-testid="filter-min-price"
              type="number"
              placeholder="Min ₺"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm outline-none focus:border-amber-400"
            />
            <input
              data-testid="filter-max-price"
              type="number"
              placeholder="Max ₺"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm outline-none focus:border-amber-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="filter-price-field"
              value={priceField}
              onChange={(e) => setPriceField(e.target.value)}
              className="px-3 py-2 border border-stone-200 rounded-lg bg-white text-sm outline-none focus:border-amber-400 flex-1"
            >
              <option value="sale_price">Satış fiyatı</option>
              <option value="cost_price">Alış fiyatı</option>
            </select>
            <button
              data-testid="apply-filters"
              onClick={load}
              className="px-3 py-2 rounded-lg bg-stone-900 text-white text-sm hover:bg-stone-800"
            >
              Uygula
            </button>
          </div>
        </div>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-xs uppercase tracking-wider text-stone-500">
                <th className="px-3 py-3 text-left">#</th>
                <th className="px-3 py-3 text-left">Fotoğraf</th>
                <th className="px-3 py-3 text-left">Ürün Adı</th>
                <th className="px-3 py-3 text-left">Özellik</th>
                <th className="px-3 py-3 text-left">Stok</th>
                <th className="px-3 py-3 text-right">Alış</th>
                <th className="px-3 py-3 text-right">Satış</th>
                <th className="px-3 py-3 text-left">Açıklama</th>
                <th className="px-3 py-3 text-left">Ürün Kodu</th>
                <th className="px-3 py-3 text-left">Barkod</th>
                <th className="px-3 py-3 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading && (
                <tr>
                  <td colSpan={11} className="text-center py-10 text-stone-400">
                    Yükleniyor...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-14 text-stone-500">
                    Hiç ürün bulunamadı.{" "}
                    <Link to="/yeni-urun" className="text-amber-700 underline">
                      Ürün ekleyin
                    </Link>
                    .
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((p, idx) => (
                  <tr
                    key={p.id}
                    data-testid={`product-row-${p.sku}`}
                    className="hover:bg-amber-50/30 transition-colors"
                  >
                    <td className="px-3 py-3 text-xs text-stone-500 font-mono-code">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-3">
                      <ProductImage path={p.image_path} size={44} alt={p.name} />
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm font-medium text-stone-900">
                        {p.name}
                      </div>
                      <div className="text-[11px] text-amber-800/70">
                        {p.category}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm text-stone-600 max-w-[180px] truncate">
                      {p.feature}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono-code text-sm text-stone-800">
                          {p.stock}
                        </span>
                        <StockBadge stock={p.stock} min={p.min_stock} />
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right text-sm text-stone-700 font-mono-code">
                      {formatTL(p.cost_price)}
                    </td>
                    <td className="px-3 py-3 text-right text-sm text-amber-800 font-semibold font-mono-code">
                      {formatTL(p.sale_price)}
                    </td>
                    <td className="px-3 py-3 text-sm text-stone-600 max-w-[220px] truncate">
                      {p.description}
                    </td>
                    <td className="px-3 py-3 text-xs font-mono-code text-stone-600">
                      {p.sku}
                    </td>
                    <td className="px-3 py-3 text-xs font-mono-code text-stone-600">
                      {p.barcode}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <button
                          data-testid={`label-${p.sku}`}
                          onClick={() => setLabelFor(p)}
                          title="Etiket Yazdır"
                          className="p-1.5 rounded-md border border-amber-200 text-amber-800 hover:bg-amber-50"
                        >
                          <Tag className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`pdf-${p.sku}`}
                          onClick={() => downloadPdf(p)}
                          title="Barkod PDF İndir"
                          className="p-1.5 rounded-md border border-stone-300 text-stone-700 hover:bg-stone-50"
                        >
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`stock-inc-${p.sku}`}
                          onClick={() => adjust(p, 1)}
                          title="Stok artır"
                          className="p-1.5 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`stock-dec-${p.sku}`}
                          onClick={() => adjust(p, -1)}
                          title="Stok azalt"
                          className="p-1.5 rounded-md border border-amber-200 text-amber-700 hover:bg-amber-50"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`edit-${p.sku}`}
                          onClick={() => setEditing(p)}
                          title="Düzenle"
                          className="p-1.5 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`delete-${p.sku}`}
                          onClick={() => removeItem(p)}
                          title="Sil"
                          className="p-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditModal
          product={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={(u) => {
            setItems((prev) => prev.map((it) => (it.id === u.id ? u : it)));
            setEditing(null);
          }}
        />
      )}
      {labelFor && (
        <LabelPrintModal
          product={labelFor}
          onClose={() => setLabelFor(null)}
        />
      )}
    </div>
  );
}

